import React, { useEffect } from "react";
import { useController } from "react-hook-form";
import { useThemedStyletron } from "@bluecrew/blueprint-web";
import styled from "styled-components";
import { FieldLabelRow } from "../../FieldLabelRow";
import { FieldRowWrapper } from "../../FieldRowWrapper";
import { RelativeFlexFieldWrapper } from "../../RelativeFlexFieldWrapper";
import { RadioList } from "../../../../components/RadioList";
import { Message, MessageVariants } from "../../../../components/Message";
import { Spacer, SpacerType } from "../../../../components/Spacer";
import { ErrorMessage } from "../../ErrorMessage";
import {
  drugTestOptions,
  DRUG_TEST_MESSAGES,
  DrugTestType,
} from "../../../../../shared/drugTestConstants";

const fieldName = "drugTestSelection";

export type DrugTestRequirementsFieldProps = {
  control: any;
  trigger: any;
  drugTestRequired: boolean;
  exemptStates: string[];
  positionState?: string;
  isLoading?: boolean;
  errors?: any;
};

export const DrugTestRequirementsField = ({
  control,
  trigger,
  drugTestRequired,
  exemptStates,
  positionState,
  isLoading = false,
  errors,
}: DrugTestRequirementsFieldProps) => {
  const [, theme] = useThemedStyletron();
  const defaultIndex = drugTestOptions.findIndex(
    (o) => o.value === DrugTestType.FIVE_PANEL_NO_THC,
  );

  const isExemptState = Boolean(positionState && exemptStates.includes(positionState));
  const isSelectionRequired = drugTestRequired && !isExemptState && !isLoading;

  const {
    field: { onChange, value },
    meta: { invalid },
  } = useController({
    name: fieldName,
    control,
    defaultValue: undefined,
    rules: {
      required: isSelectionRequired ? "Please select a drug test requirement" : false,
    },
  });

  const selectedIndex =
    // eslint-disable-next-line no-nested-ternary
    value !== undefined
      ? drugTestOptions.findIndex((o) => o.value === value)
      : isSelectionRequired
        ? defaultIndex
        : undefined;

  useEffect(() => {
    if (isSelectionRequired) {
      if (value === undefined) {
        onChange(DrugTestType.FIVE_PANEL_NO_THC);
      }
    } else if (value !== undefined) {
      onChange(undefined);
    }
  }, [isSelectionRequired, value, onChange]);

  if (!drugTestRequired || isLoading) {
    return null;
  }

  if (isExemptState) {
    return (
      <RelativeFlexFieldWrapper data-pendo-key="DrugTestRequirementsExemptInfo">
        <FieldRowWrapper>
          <FieldLabelRow>{DRUG_TEST_MESSAGES.FIELD_LABEL}</FieldLabelRow>
          <Spacer $type={SpacerType.horizontal} $size={theme.sizing.scale600} />
          <InfoWrapper>
            <Message
              text={DRUG_TEST_MESSAGES.STATE_EXEMPT(positionState as string)}
              variant={MessageVariants.Neutral}
            />
          </InfoWrapper>
        </FieldRowWrapper>
      </RelativeFlexFieldWrapper>
    );
  }

  const errorMessage = errors?.[fieldName]?.message;

  const handleSelectionChange = (index: number) => {
    onChange(drugTestOptions[index].value);
    trigger(fieldName);
  };

  return (
    <RelativeFlexFieldWrapper data-pendo-key="DrugTestRequirementsField">
      <FieldRowWrapper>
        <FieldLabelRow>
          {DRUG_TEST_MESSAGES.FIELD_LABEL}<RequiredIndicator>*</RequiredIndicator>
        </FieldLabelRow>
        <Spacer $type={SpacerType.horizontal} $size={theme.sizing.scale600} />
        <FieldContainer>
          <RadioListWrapper hasError={invalid}>
            <RadioList
              options={drugTestOptions}
              selected={selectedIndex}
              setSelected={handleSelectionChange}
            />
          </RadioListWrapper>
          {invalid && errorMessage && <ErrorMessage text={errorMessage} />}
        </FieldContainer>
      </FieldRowWrapper>
    </RelativeFlexFieldWrapper>
  );
};

const InfoWrapper = styled.div`
  width: 502.82px;
  padding: 12px;
  background-color: #f0f0f0;
  border-radius: 4px;
`;

const FieldContainer = styled.div`
  width: 502.82px;
`;

const RadioListWrapper = styled.div<{ hasError?: boolean }>`
  width: 100%;
  border: ${({ hasError, theme }) => 
    hasError ? `2px solid ${theme.colors.negative}` : 'none'};
  border-radius: ${({ hasError }) => hasError ? '4px' : '0'};
  padding: ${({ hasError }) => hasError ? '8px' : '0'};
  transition: border-color 0.2s ease;
`;

const RequiredIndicator = styled.span`
  color: ${({ theme }) => theme.colors.negative};
  margin-left: 4px;
`;
